import axios from 'axios';
import ENV from 'constants/env';
import { getActiveCompanyId } from 'utils/companyContext';

const timeOffBase = ENV.TIMEOFF_BASE_URL || (window.location.origin + '/timeoff/api');
const timeOffApi = axios.create({
    baseURL: timeOffBase,
    headers: {
        'X-API-key': ENV.API_KEY,
    },
});

timeOffApi.interceptors.request.use((config) => {
    const cid = getActiveCompanyId();
    const urlPath = config.url || '';
    const isUserScopedNotification = /\/notifications/i.test(urlPath)
        && /recipientType=user/i.test(urlPath);
    // Accountants may have no company selected yet; still allow user-scoped notification polls.
    if (!cid && !isUserScopedNotification) {
        return Promise.reject(new axios.Cancel('No company selected'));
    }
    if (cid && (/companyId=null|companyId=undefined/i.test(urlPath))) {
        return Promise.reject(new axios.Cancel('No company selected'));
    }
    const token = localStorage.getItem('authToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

const companyId = () => getActiveCompanyId();

// Policies
export const getPolicies = async () =>
    (await timeOffApi.get(`/policies?companyId=${companyId()}`)).data;

export const createPolicy = async (data) =>
    (await timeOffApi.post('/policies', { ...data, companyId: companyId() })).data;

export const updatePolicy = async ({ id, ...data }) =>
    (await timeOffApi.put(`/policies/${id}`, { ...data, companyId: companyId() })).data;

export const deletePolicy = async (id) =>
    (await timeOffApi.delete(`/policies/${id}`)).data;

// Balances
export const getBalances = async (employeeId) => {
    const params = employeeId
        ? `companyId=${companyId()}&employeeId=${employeeId}`
        : `companyId=${companyId()}`;
    return (await timeOffApi.get(`/balances?${params}`)).data;
};

export const createBalance = async (data) =>
    (await timeOffApi.post('/balances', { ...data, companyId: companyId() })).data;

export const adjustBalance = async ({ id, ...data }) =>
    (await timeOffApi.patch(`/balances/${id}`, data)).data;

export const deleteBalance = async (id) =>
    (await timeOffApi.delete(`/balances/${id}`)).data;

// Requests
export const getRequests = async ({ employeeId, status } = {}) => {
    const params = new URLSearchParams({ companyId: companyId() });
    if (employeeId) params.set('employeeId', employeeId);
    if (status) params.set('status', status);
    return (await timeOffApi.get(`/requests?${params}`)).data;
};

export const getTimeOffByPeriod = async ({ startDate, endDate }) =>
    (await timeOffApi.get(`/requests/by-period?companyId=${companyId()}&startDate=${startDate}&endDate=${endDate}`)).data;

export const createRequest = async (data) =>
    (await timeOffApi.post('/requests?adminOverride=true', { ...data, companyId: companyId() })).data;

export const approveRequest = async ({ id, reviewNote }) =>
    (await timeOffApi.patch(`/requests/${id}/approve`, { reviewNote })).data;

export const denyRequest = async ({ id, reviewNote }) =>
    (await timeOffApi.patch(`/requests/${id}/deny`, { reviewNote })).data;

export const markRequestPaid = async (id) =>
    (await timeOffApi.patch(`/requests/${id}/mark-paid`, {})).data;

export const deleteRequest = async (id) =>
    (await timeOffApi.delete(`/requests/${id}`)).data;

// Notifications
export const getNotifications = async ({ recipientId, recipientType }) => {
    const params = new URLSearchParams({ recipientId, recipientType });
    const cid = companyId();
    // User-scoped firm alerts must not be filtered by the currently selected client company.
    if (cid && recipientType !== 'user') params.set('companyId', cid);
    return (await timeOffApi.get(`/notifications?${params}`)).data;
};

export const createNotification = async (data) =>
    (await timeOffApi.post('/notifications', { ...data, companyId: companyId() })).data;

export const markNotificationRead = async (id) =>
    (await timeOffApi.patch(`/notifications/${id}/read`, {})).data;

export const markAllNotificationsRead = async ({ recipientId, recipientType }) =>
    (await timeOffApi.patch(`/notifications/read-all?recipientId=${recipientId}&recipientType=${recipientType}`, {})).data;

export const deleteNotification = async (id) =>
    (await timeOffApi.delete(`/notifications/${id}`)).data;

// Company Holidays
export const getHolidays = async () =>
    (await timeOffApi.get(`/holidays?companyId=${companyId()}`)).data;

export const createHoliday = async (data) =>
    (await timeOffApi.post('/holidays', { ...data, companyId: companyId() })).data;

export const updateHoliday = async ({ id, ...data }) =>
    (await timeOffApi.put(`/holidays/${id}`, { ...data, companyId: companyId() })).data;

export const deleteHoliday = async (id) =>
    (await timeOffApi.delete(`/holidays/${id}`)).data;

// Accruals
export const runAccruals = async ({ payPeriodStart, payPeriodEnd, employees }) =>
    (await timeOffApi.post('/accruals/run', {
        companyId: companyId(),
        payPeriodStart,
        payPeriodEnd,
        employees,
    })).data;

// Employees proxy (avoids CORS)
export const getEmployeesForTimeOff = async (cId) =>
    (await timeOffApi.get(`/employees-proxy?companyId=${cId ?? companyId()}`)).data;
